import { AssetType, getAssetDetailsByIdAndType } from '@nemuru-eng/js-api-client';

import { AutoDetailsForm } from '@/components/modules/AssetDetails/components/LoanAssetDetails/AutoDetailsForm/AutoDetailsForm';
import { AutoDetailsFormSchema } from '@/components/modules/AssetDetails/components/LoanAssetDetails/AutoDetailsForm/schemas/AutoDetailsFormSchema';
import { CardDetailsForm } from '@/components/modules/AssetDetails/components/LoanAssetDetails/CardDetailsForm/CardDetailsForm';
import { ConsumerLoanDetailsDefaultForm } from '@/components/modules/AssetDetails/components/LoanAssetDetails/ConsumerLoanDetailsDefaultForm/ConsumerLoanDetailsDefaultForm';
import { ConsumerLoanDetailsFormSchema } from '@/components/modules/AssetDetails/components/LoanAssetDetails/ConsumerLoanDetailsDefaultForm/schemas/ConsumerLoanDetailsFormSchema';
import { EducationDetailsForm } from '@/components/modules/AssetDetails/components/LoanAssetDetails/EducationDetailsForm/EducationDetailsForm';
import { EducationDetailsFormSchema } from '@/components/modules/AssetDetails/components/LoanAssetDetails/EducationDetailsForm/schemas/EducationDetailsFormSchema';
import { HomeImprovementDetailsForm } from '@/components/modules/AssetDetails/components/LoanAssetDetails/HomeImprovementDetailsForm/HomeImprovementDetailsForm';
import { HomeImprovementDetailsFormSchema } from '@/components/modules/AssetDetails/components/LoanAssetDetails/HomeImprovementDetailsForm/schemas/HomeImprovementDetailsFormSchema';
import { UtilitiesSupplyDetailsFormSchema } from '@/components/modules/AssetDetails/components/LoanAssetDetails/UtilitiesSupplyDetailsForm/schemas/UtilitiesSupplyDetailsFormSchema';
import { UtilitiesSupplyDetailsForm } from '@/components/modules/AssetDetails/components/LoanAssetDetails/UtilitiesSupplyDetailsForm/UtilitiesSupplyDetailsForm';
import { AutoDetailsDataSkeleton } from '@/components/modules/AssetDetails/components/shared/AutoDetailsDataSkeleton';
import { DetailsDataSkeleton } from '@/components/modules/AssetDetails/components/shared/DetailsDataSkeleton';
import { useAssetDetails } from '@/components/modules/AssetDetails/hooks/useAssetDetails';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { PendingDataType } from '@/config/types';
import { useCache } from '@/hooks/requests/useCache';

interface ConsumerLoanDetailsProps {
  assetId: string;
  assetType: AssetType;
  editModeAllowed?: boolean;
}

export const LoanAssetDetails = ({ assetId, assetType, editModeAllowed = false }: ConsumerLoanDetailsProps) => {
  const { opened, toggleDialog, setOpened } = useDialog();
  const { data: assetDetails, loading: assetDetailsLoading } = useCache(getAssetDetailsByIdAndType, {
    assetId,
    assetType,
  });
  const { patchAssetDetails } = useAssetDetails();

  if (assetDetailsLoading) {
    if (assetType === AssetType.AUTO) {
      return <AutoDetailsDataSkeleton />;
    }

    return <DetailsDataSkeleton numberOfRows={3} pt={3} />;
  }

  const toggleEditMode = editModeAllowed && assetDetails ? toggleDialog : undefined;

  const onSubmitForm = async (data) => {
    if (patchAssetDetails) await patchAssetDetails(data); //TODO => missing assetDetailsEdit function to be
    toggleDialog();
  };

  if (!assetDetails) return <PendingDataWarning type={PendingDataType.DEFAULT} />;

  const getModuleByAssetDetailsType = (isEditable?: boolean) => {
    const fieldVariant = isEditable ? FieldVariant.form : FieldVariant.dataRow;

    switch (assetDetails.type) {
      case AssetType.AUTO:
        return {
          component: <AutoDetailsForm assetDetails={assetDetails} fieldVariant={fieldVariant} fullWidth={isEditable} />,
          defaultValues: {
            type: assetDetails.type,
            description: assetDetails.description,
            amount: assetDetails.value.amount,
            brand: assetDetails.brand,
            model: assetDetails.model,
            modelVersion: assetDetails.modelVersion,
            licensePlateNumber: assetDetails.usage?.plateNumber,
            identificationNumber: assetDetails.specs?.identificationNumber,
            registrationDate: assetDetails.specs?.registrationDate,
            power: assetDetails.specs?.power,
            engine: assetDetails.specs?.engine,
            fuelType: assetDetails.specs?.fuelType,
            category: assetDetails.category,
            bodyType: assetDetails.specs?.bodyType,
            gear: assetDetails.specs?.gear,
            isNewVehicle: assetDetails.isNewVehicle,
            imported: assetDetails.specs?.imported,
          },
          formSchema: AutoDetailsFormSchema,
        };
      case AssetType.EDUCATION:
        return {
          component: (
            <EducationDetailsForm assetDetails={assetDetails} fullWidth={isEditable} fieldVariant={fieldVariant} />
          ),
          defaultValues: {
            type: assetDetails.type,
            description: assetDetails.description,
            startDate: assetDetails.startDate,
            endDate: assetDetails.endDate,
            value: assetDetails.value.amount,
            downPayment: assetDetails.downPayment?.amount,
            scholarship: assetDetails.scholarship?.amount,
          },
          formSchema: EducationDetailsFormSchema,
        };
      case AssetType.HOME_IMPROVEMENT:
        return {
          component: (
            <HomeImprovementDetailsForm
              assetDetails={assetDetails}
              fullWidth={isEditable}
              fieldVariant={fieldVariant}
            />
          ),
          defaultValues: {
            type: assetDetails.type,
            description: assetDetails.description,
            workSiteAddress: assetDetails.workSiteAddress,
          },
          formSchema: HomeImprovementDetailsFormSchema,
        };
      case AssetType.UTILITIES_SUPPLY:
        return {
          component: (
            <UtilitiesSupplyDetailsForm
              assetDetails={assetDetails}
              fullWidth={isEditable}
              fieldVariant={fieldVariant}
            />
          ),
          defaultValues: { type: assetDetails.type, description: assetDetails.description, cups: assetDetails.cups },
          formSchema: UtilitiesSupplyDetailsFormSchema,
        };
      default:
        return {
          component: (
            <ConsumerLoanDetailsDefaultForm
              assetDetails={assetDetails}
              fullWidth={isEditable}
              fieldVariant={fieldVariant}
            />
          ),
          defaultValues: { type: assetDetails.type, description: assetDetails.description },
          formSchema: ConsumerLoanDetailsFormSchema,
        };
    }
  };

  return (
    <SubSectionWrapper toggleEditModeDialog={toggleEditMode}>
      {getModuleByAssetDetailsType(false).component}
      <EditDialogWrapper
        opened={opened}
        setOpened={setOpened}
        handleClose={toggleDialog}
        DialogProps={{
          maxWidth: 'sm',
          fullWidth: true,
        }}
        title={'EDIT_CONSUMER_LOAN_DETAILS'}
        onSubmitForm={onSubmitForm}
        defaultValues={getModuleByAssetDetailsType().defaultValues}
        formSchema={getModuleByAssetDetailsType().formSchema}
      >
        {getModuleByAssetDetailsType(true).component}
      </EditDialogWrapper>
    </SubSectionWrapper>
  );
};
